import moment from "moment";
import * as Yup from "yup";


export const eventInformation = Yup.object().shape({
  eventName: Yup.string().required("Required"),
  registrationType: Yup.string().when(["multiDateEvent", "displayOnly"], {
    is: (multiDateEvent, displayOnly) => multiDateEvent && !displayOnly,
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  date: Yup.date().nullable().transform((value, originalValue) => {
    if (!originalValue || originalValue === '') {
      return undefined;
    }
    return value;
  }).required("Required"),
  eventDuration: Yup.object().shape({
    key: Yup.string().required("Required"),
    value: Yup.number().required("Required"),
  }),
  eventStartDate: Yup.string().nullable().when("displayOnly", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Required"),
  }),
  eventEndDate: Yup.string().nullable().when("displayOnly", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Required"),
  }),
  dateLabel: Yup.string().when('multiDateEvent', {
    is: true,
    then: (schema) => schema
      .required('Required')
      .test('unique-date-label', 'Label already used', function(value) {
        if (!value) return true;
        const additionalDates = this.parent?.additionalEventDates || [];
        return !additionalDates.some(
          (d) => d?.label && d.label.trim().toLowerCase() === value.trim().toLowerCase()
        );
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  additionalEventDates: Yup.array().when('multiDateEvent', {
    is: true,
    then: (schema) => schema.of(
      Yup.object().shape({
        label: Yup.string()
          .required('Required')
          .test('unique-additional-label', 'Label already used', function(value) {
            if (!value) return true;
            // from[0] = current item object, from[1] = root form values
            const root = this.from?.[1]?.value;
            if (!root) return true;
            const currentIndexMatch = this.path.match(/\[(\d+)\]/);
            const currentIndex = currentIndexMatch ? parseInt(currentIndexMatch[1]) : -1;
            const primaryLabel = root?.dateLabel;
            if (primaryLabel && value.trim().toLowerCase() === primaryLabel.trim().toLowerCase()) return false;
            const additionalDates = root?.additionalEventDates || [];
            return !additionalDates.some(
              (d, idx) => idx !== currentIndex && d?.label && d.label.trim().toLowerCase() === value.trim().toLowerCase()
            );
          }),
      })
    ),
    otherwise: (schema) => schema.notRequired(),
  }),
  eventDetails: Yup.object().shape({
    actionButtonLabel: Yup.string().test('action-btn-required', 'Required', function(value) {
      const root = this.from?.[1]?.value;
      if (root?.displayOnly) return true;
      return !!value;
    }),
    infiniteCapacity: Yup.boolean(),
    maximumCapacity: Yup.number()
      .nullable()
      .test('max-capacity-required', 'Required', function(value) {
        const root = this.from?.[1]?.value;
        if (root?.displayOnly) return true;
        if (this.parent?.infiniteCapacity) return true;
        return value !== undefined && value !== null && value !== '' && Number(value) >= 1;
      }),
    contactInfo: Yup.object().shape({
      email: Yup.string().email("Invalid email").required('Required'),
    }),
  }),
});

const priceItemSchema = Yup.object().shape({
  amount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .when(["type", "variant"], {
      is: (type, variant) => (type === "Tickets" || type === "Addons") && !variant,
      then: (schema) =>
        schema.typeError("Required").required("Required").min(0, "Amount must be greater than or equal to 0"),
      otherwise: (schema) => schema.notRequired(),
    }),
  quantity: Yup.mixed().test("quantity-required", "Required", function (value) {
    const { type, variant, infiniteQuantity, limitType } = this.parent;
    const isInfinite = infiniteQuantity === true || infiniteQuantity === "true";
    const isCheckbox = limitType === "checkbox";
    if (type === "Addons" && !variant && !isInfinite && !isCheckbox) {
      const num = Number(value);
      return value !== undefined && value !== null && value !== "" && !isNaN(num) && num >= 1;
    }
    if (type === "Open Amount" && !isInfinite) {
      const num = Number(value);
      return value !== undefined && value !== null && value !== "" && !isNaN(num) && num >= 1;
    }
    return true;
  }),
  minimumOpenAmount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .when("type", {
      is: "Open Amount",
      then: (schema) =>
        schema.typeError("Required").required("Required").min(1, "Must be greater than 0"),
      otherwise: (schema) => schema.notRequired(),
    }),
  variantInfo: Yup.array().when(["type", "variant"], {
    is: (type, variant) => (type === "Tickets" || type === "Addons") && variant === true,
    then: (schema) =>
      schema.min(1, "At least one variant is required").of(
        Yup.object().shape({
          name: Yup.string().trim().test(
            'variant-name-required',
            'Required',
            function(value) {
              const item = this.parent;
              // isMultiDateVariant rows only need a name if there's a user-added variant for the same date
              if (item?.isMultiDateVariant) {
                const variantInfo = this.from?.[1]?.value?.variantInfo || [];
                const hasManualForDate = variantInfo.some(
                  v => !v.isMultiDateVariant && String(v.eventDate) === String(item.eventDate)
                );
                if (!hasManualForDate) return true;
              }
              return !!value?.trim();
            }
          ).test(
            'unique-variant-name',
            'Variant name already exists',
            function(value) {
              if (!value?.trim()) return true;
              const variantInfo = this.from?.[1]?.value?.variantInfo || [];
              const matches = this.path.match(/variantInfo\[(\d+)\]/);
              const currentIndex = matches ? parseInt(matches[1]) : -1;
              return !variantInfo.some(
                (v, i) => i !== currentIndex && v?.name?.trim().toLowerCase() === value.trim().toLowerCase()
              );
            }
          ),
          amount: Yup.number()
            .transform((value, originalValue) =>
              originalValue === "" ? undefined : Number(originalValue),
            )
            .typeError("Required")
            .required("Required")
            .min(0, "Amount must be greater than or equal to 0"),
          quantity: Yup.number()
            .transform((value, originalValue) =>
              originalValue === "" || originalValue === null ? undefined : Number(originalValue),
            )
            .when("infiniteQuantity", {
              is: (val) => !val,
              then: (s) => s.typeError("Required").required("Required").min(1, "Must be greater than 0"),
              otherwise: (s) => s.notRequired().nullable(),
            }),
          eventDate: Yup.mixed().when("requiresEventDate", {
            is: true,
            then: (s) => s.required("Required"),
            otherwise: (s) => s.notRequired(),
          }),
        }),
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  installmentDetailsTransaction: Yup.object().when("isInstallmentTransaction", {
    is: true,
    then: (schema) =>
      schema.shape({
        numberOfInstallment: Yup.number()
          .nullable()
          .typeError("Required")
          .required("Required")
          .min(2, "Required"),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  ledger: Yup.object().when("type", {
    is: (type) => type === "Tickets" || type === "Addons" || type === "Open Amount",
    then: (schema) =>
      schema.nullable().test(
        "ledger-required",
        "Required",
        (value) => value && Object.keys(value).length > 0,
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const discountItemSchema = Yup.object().shape({
  discount_name: Yup.string()
    .required("Required")
    .test('unique-discount-name', 'Discount name already exists', function(value) {
      if (!value?.trim()) return true;
      const allDiscounts = this.from?.[1]?.value?.discount || [];
      const match = this.path.match(/\[(\d+)\]/);
      const currentIndex = match ? parseInt(match[1]) : -1;
      return !allDiscounts.some((d, i) =>
        i !== currentIndex &&
        d?.discount_name?.trim()?.toLowerCase() === value.trim().toLowerCase()
      );
    }),
  discount_type: Yup.string().required("Required"),
  price_type: Yup.string().required("Required"),
  value: Yup.string().required("Required"),
  amount: Yup.string().when('discount_type', {
    is: 'Amount',
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
  quantity: Yup.string().when('discount_type', {
    is: 'Quantity',
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
  start_date: Yup.string().nullable().when('discount_type', {
    is: 'Date',
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
  end_date: Yup.string().nullable().when('discount_type', {
    is: 'Date',
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

export const eventPricing = Yup.object().shape({
  prices: Yup.array().when("freeEvent", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.of(priceItemSchema),
  }),
  discount: Yup.array().when("freeEvent", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => Yup.lazy((value) => {
      if (!value || value.length === 0) {
        return Yup.array();
      }
      return Yup.array().of(discountItemSchema);
    }),
  }),
});
